import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { salesAPI, customerAPI, productAPI, metaAPI, deliveryAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import AsyncSearchableSelect from '../../components/AsyncSearchableSelect';
import SearchableSelect from '../../components/SearchableSelect';
import { toast } from 'sonner';
import TableTopControls from "../../components/TableTopControls";
import TableBottomControls from "../../components/TableBottomControls";
import { Plus, Edit, Trash2 } from 'lucide-react';
import ConfirmModal from "../../components/ConfirmModal";
import useTime from "../../hooks/UseTime";
import { Calendar } from "@/components/ui/calendar";

export default function SalesAdmin() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { formatDateTime, fromNow } = useTime();
  const [formData, setFormData] = useState({
    customer_id: '',
    items: [
      { product_id: '', product_name: '', quantity: 1, price: 0 }
    ],
    payment_status: '',
    payment_method: '',
    delivery_required: false,
    sale_date: new Date()
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({customer_id: '',product_id: '',payment_status: '',delivery_status: '',from_date: null,to_date: null});
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [editingSale, setEditingSale] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [salesTotal, setSalesTotal] = useState(0);
  const [originalItems, setOriginalItems] = useState([]);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [open, setOpen] = useState(false);
  const formatRange = (from, to) => {
    if (!from && !to) return "";
    if (from && !to) {
      return `${from.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })} onwards`;
    }

    if (!from && to) {
      return `Until ${to.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })}`;
    }

    const optionsDay = { day: "2-digit" };
    const optionsMonth = { month: "short" };
    const optionsFull = { day: "2-digit", month: "short" };
    const optionsYear = { year: "numeric" };

    const sameDay =
      from.toDateString() === to.toDateString();

    const sameMonth =
      from.getMonth() === to.getMonth() &&
      from.getFullYear() === to.getFullYear();

    const sameYear =
      from.getFullYear() === to.getFullYear();

    if (sameDay) {
      return from.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }

    if (sameMonth) {
      return `${from.toLocaleDateString("en-GB", optionsDay)}–${to.toLocaleDateString("en-GB", optionsDay)} ${to.toLocaleDateString("en-GB", optionsMonth)}`;
    }

    if (sameYear) {
      return `${from.toLocaleDateString("en-GB", optionsFull)} → ${to.toLocaleDateString("en-GB", optionsFull)}`;
    }

    return `${from.toLocaleDateString("en-GB", {
      ...optionsFull,
      ...optionsYear
    })} → ${to.toLocaleDateString("en-GB", {
      ...optionsFull,
      ...optionsYear
    })}`;
  };
  const formatDate = (date) => {
    if (!date) return undefined;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".date-picker")) {
        setOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchMeta = async () => {
    try {
      const [paymentRes, deliveryRes, paymentMethodsRes, customersRes, productsRes] = await Promise.all([
        metaAPI.getPaymentStatuses(),
        metaAPI.getDeliveryStatuses(),
        metaAPI.getPaymentMethods(),
        customerAPI.getAll(),
        productAPI.getAll()
      ]);

      setPaymentOptions(paymentRes.data);
      setDeliveryOptions(deliveryRes.data);
      setPaymentMethods(paymentMethodsRes.data);
      setCustomers(customersRes.data.data);
      setProducts(productsRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const [salesRes] = await Promise.all([
        salesAPI.get({
          page,
          page_size: pageSize,
          tz,
          ...appliedFilters,
          from_date: formatDate(appliedFilters.from_date),
          to_date: formatDate(appliedFilters.to_date),
        })
      ]);

      setSales(salesRes.data.items);
      setTotalPages(salesRes.data.total_pages);
      setSalesTotal(salesRes.data.total_items);
      setTotalSalesAmount(salesRes.data.total_sales_amount || 0);

    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedFilters]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    const empty = {
      customer_id: '',
      product_id: '',
      payment_status: '',
      delivery_status: '',
      from_date: null,
      to_date: null
    };

    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMeta(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const customer = customers.find(c => c.id === formData.customer_id);
      if (!customer) {
        toast.error('Customer is required');
        return;
      }

      const originalMap = Object.fromEntries(originalItems.map(i => [String(i.product_id), i.quantity]));
      const productMap = Object.fromEntries(products.map(p => [p.id, p]));

      const items = formData.items.map(item => {
        
        const product = productMap[item.product_id];
        if (!product) throw new Error('Invalid product');

        const oldQty = originalMap[item.product_id] || 0;
        const diff = item.quantity - oldQty;

        if (diff > 0 && product.stock < diff) {
          throw new Error(`Only ${product.stock} extra units available for ${product.name}`);
        }

        const total = item.price * item.quantity;

        return {
          product_id: product.id,
          product_name: product.name,
          quantity: item.quantity,
          price: item.price,
          total
        };
      });

      if(items.some(i => i.quantity <= 0)) {
        toast.error('Quantity must be at least 1');
        return;
      }

      if(items.some(i => i.price < 0)) {
        toast.error('Price cannot be negative');
        return;
      }

      if(items.length === 0) {
        toast.error('At least one product must be added');
        return;
      }

      if(formData.payment_status === '') {
        toast.error('Payment status is required');
        return;
      }

      if(
        (formData.payment_status == 'paid' || formData.payment_status == 'partial')
        && formData.payment_method === ''
      ) {
        toast.error('Payment method is required for paid or partial payments');
        return;
      }

      if (new Set(formData.items.map(i => i.product_id)).size !== formData.items.length) {
        toast.error('Duplicate products not allowed');
        return;
      }

      if(formData.sale_date && formData.sale_date > new Date()) {
        toast.error('Sale date cannot be in the future');
        return;
      }

      const total_amount = items.reduce((sum, i) => sum + i.total, 0);

      const saleData = {
        customer_id: formData.customer_id,
        items,
        total_amount,
        payment_status: formData.payment_status,
        payment_method: formData.payment_method,
        delivery_required: formData.delivery_required,
        delivery_address: formData.delivery_required
          ? customer?.addresses?.[0]?.street
          : null,
        sale_date: toSaleUTC(formData.sale_date)
      };

      if(editingSale) {
        await salesAPI.update(editingSale.id, saleData);
        toast.success('Sale updated successfully');
      } else {
        await salesAPI.create(saleData);
        toast.success('Sale recorded successfully');
      }

      setShowForm(false);
      setEditingSale(null);
      setFormData({
        customer_id: '',
        items: [{ product_id: '', product_name: '', quantity: 1, price: 0 }],
        payment_status: '',
        payment_method: '',
        delivery_required: false,
        sale_date: new Date()
      });

      fetchData();
    } catch (error) {
      toast.error(error.message || error.response?.data?.detail || 'Failed to record sale');
    }
  };

  const toSaleUTC = (date) => {
    if (!date) return null;

    const selected = new Date(date);
    const now = new Date();

    const isToday =
      selected.getFullYear() === now.getFullYear() &&
      selected.getMonth() === now.getMonth() &&
      selected.getDate() === now.getDate();

    if (isToday) {
      return now.toISOString();
    }

    return new Date(
      selected.getFullYear(),
      selected.getMonth(),
      selected.getDate(),
      0, 0, 0
    ).toISOString();
  };

  const handleEdit = (sale) => {
    setOriginalItems(sale.items);
    setEditingSale(sale);
    setFormData({
      customer_id: sale.customer_id,
      items: sale.items.map(item => ({ ...item })),
      payment_status: sale.payment_status,
      payment_method: sale.payment_method,
      delivery_required: sale.delivery_required,
      delivery_address: sale.delivery_address || '',
      sale_date: sale.created_at ? new Date(sale.created_at) : new Date()
    });
    setShowForm(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await salesAPI.delete(deleteId);
      toast.success("Sale deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sale');
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', product_name: '', quantity: 1, price: 0 }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    const updated = [...formData.items];
    updated[index][field] = value;

    // auto-fill price when product selected
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        updated[index].price = product.price;
        updated[index].product_name = product.name;
      }
    }

    setFormData({ ...formData, items: updated });
  };

  const totalAmount = formData.items.reduce((sum, item) => {
    return sum + ((item.price || 0) * (item.quantity || 0));
  }, 0);

  const handleStatusChange = async (id, status) => {
    try {
      await deliveryAPI.updateStatus(id, status);
      toast.success('Delivery status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <AdminLayout activeItem="Sales">
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-medium text-brand-primary font-outfit">
            Sales (Rs. {totalSalesAmount.toLocaleString()}) 
            <span className="text-sm text-slate-500 ml-2">
              • {salesTotal} records
            </span>
          </h1>
          {!isSuperAdmin && (
            <button onClick={() => {
              setShowForm(true)
              setEditingSale(null);
              setFormData({
                customer_id: '',
                items: [{ product_id: '', product_name: '', quantity: 1, price: 0 }],
                payment_status: '',
                payment_method: '',
                delivery_required: false,
                sale_date: new Date()
              });
            }}
              className="flex items-center bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200"
              data-testid="add-sale-button">
              <Plus className="h-5 w-5 mr-2" /> Record Sale
            </button>
          )}
        </div>
      </header>

      <main className="p-8" data-testid="sales-admin-page">
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="sale-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
            <div className="bg-white p-8 max-w-3xl w-full mx-4 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-semibold mb-6">Record Sale</h2>
              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Sale Date
                  </label>
                  <input
                    type="date"
                    value={formData.sale_date ? formatDate(formData.sale_date) : ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sale_date: e.target.value ? new Date(e.target.value) : null
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-200"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Customer</label>
                  <AsyncSearchableSelect
                    value={formData.customer_id}
                    selectedLabel={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_id: e })}
                    loadOptions={async (input) => {
                      const res = await customerAPI.search(input);
                      return res.data.data.map(c => ({ label: c.name, value: c.id }));
                    }}
                    placeholder="Select Customer"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Products
                  </label>

                  {formData.items.map((item, index) => (
                    <div key={index} className="flex gap-3 mb-3 items-end border-b pb-2">

                      <div className="flex-1">
                        <AsyncSearchableSelect
                          value={item.product_id}
                          selectedLabel={item.product_name}
                          onChange={(val) => updateItem(index, 'product_id', val)}
                          loadOptions={async (input) => {
                            const res = await productAPI.search(input);
                            return res.data.data.map(p => ({
                              label: `${p.name} - Rs. ${p.price} (Stock: ${p.stock})`,
                              value: p.id
                            }));
                          }}
                          placeholder="Select Product"
                        />
                      </div>

                      <div className="w-28">
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value))}
                          className="w-full px-2 py-2 border border-slate-200"
                        />
                      </div>

                      <div className="w-28">
                        <input
                          type="number"
                          min="0"
                          value={item.price}
                          onChange={(e) =>
                            updateItem(index, 'price', parseFloat(e.target.value) || 0)
                          }
                          className="w-full px-2 py-2 border border-slate-200"
                          placeholder="Price"
                        />
                      </div>

                      <div className="w-32 text-sm text-slate-600 py-2">
                        Rs. {(item.price * item.quantity) || 0}
                      </div>

                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-500 px-2"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addItem}
                    className="text-sm text-brand-accent mt-2"
                  >
                    + Add Product
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Status</label>
                  <SearchableSelect
                    value={formData.payment_status}
                    onChange={(val) => setFormData({ ...formData, payment_status: val })}
                    options={paymentOptions}
                    placeholder="Payment Status"
                  />
                </div>
                {(formData.payment_status == 'paid' || formData.payment_status == 'partial') && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label>
                    <SearchableSelect
                      value={formData.payment_method}
                      onChange={(val) => setFormData({ ...formData, payment_method: val })}
                      options={paymentMethods}
                      placeholder="Payment Method"
                    />
                  </div>
                )}
                <div className="col-span-2 flex items-center">
                  <input type="checkbox" id="delivery_required" checked={formData.delivery_required}
                    onChange={(e) => setFormData({ ...formData, delivery_required: e.target.checked })}
                    className="mr-2" data-testid="sale-delivery-checkbox" />
                  <label htmlFor="delivery_required" className="text-sm text-slate-700">Delivery Required</label>
                </div>
                <div className="col-span-2 flex justify-end text-lg font-semibold">
                  Total: Rs. {totalAmount}
                </div>
                <div className="col-span-2 flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200" data-testid="sale-submit-button">
                    {editingSale ? 'Update' : 'Record'}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setEditingSale(null); }}
                    className="flex-1 border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-2 transition-all duration-200">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200">
            
            <div className="bg-slate-50 p-4 border-b border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">

                <div>
                  <label className="block text-sm text-slate-500 mb-1">Customer</label>
                  <AsyncSearchableSelect
                    value={filters.customer_id}
                    selectedLabel={filters.customer_name}
                    onChange={(val) => setFilters({ ...filters, customer_id: val })}
                    loadOptions={async (input) => {
                      const res = await customerAPI.search(input);
                      return res.data.data.map(c => ({ label: c.name, value: c.id }));
                    }}
                    placeholder="Select Customer"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-500 mb-1">Product</label>
                  <AsyncSearchableSelect
                    value={filters.product_id}
                    selectedLabel={filters.product_name}
                    onChange={(val) => setFilters({ ...filters, product_id: val })}
                    loadOptions={async (input) => {
                      const res = await productAPI.search(input);
                      return res.data.data.map(p => ({ label: p.name, value: p.id }));
                    }}
                    placeholder="Select Product"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-500 mb-1">Payment</label>
                  <SearchableSelect
                    value={filters.payment_status}
                    onChange={(val) => setFilters({ ...filters, payment_status: val })}
                    options={paymentOptions}
                    placeholder="Payment Status"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-500 mb-1">Delivery</label>
                  <SearchableSelect
                    value={filters.delivery_status}
                    onChange={(val) => setFilters({ ...filters, delivery_status: val })}
                    options={deliveryOptions}
                    placeholder="Delivery Status"
                  />
                </div>

                <div className="relative date-picker">
                  <label className="block text-sm text-slate-500 mb-1">Date Range</label>

                  <button
                    onClick={() => setOpen(!open)}
                    className={`w-full px-3 py-2 border border-slate-200 bg-white flex items-center justify-between overflow-hidden ${
                      filters.from_date && filters.to_date
                        ? "text-slate-800"
                        : "text-slate-400"
                    }`}
                  >
                    <span className="truncate">
                      {filters.from_date || filters.to_date
                        ? formatRange(filters.from_date, filters.to_date)
                        : "Select date range"}
                    </span>

                    <svg
                      className={`w-4 h-4 text-slate-500 transition-transform ml-2 shrink-0 ${
                        open ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {open && (
                    <div className="absolute z-50 mt-2 bg-white border rounded-md shadow-lg">
                      <Calendar
                        mode="range"
                        selected={{
                          from: filters.from_date,
                          to: filters.to_date
                        }}
                        onSelect={(range) => {
                          setFilters({
                            ...filters,
                            from_date: range?.from || null,
                            to_date: range?.to || null
                          });

                          // optional: auto close when range selected
                          if (range?.from && range?.to) setOpen(false);
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-2 text-center">
                  <button
                    onClick={handleSearch}
                    className="flex-1 bg-brand-accent text-white px-3 py-2 text-sm hover:bg-brand-accent-hover"
                  >
                    Search
                  </button>

                  <button
                    onClick={handleReset}
                    className="flex-1 border border-slate-300 text-slate-700 px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    Reset
                  </button>
                </div>

              </div>
            </div>
                
            <div className="overflow-x-auto">
              <TableTopControls
                rowsPerPage={pageSize}
                setRowsPerPage={(val) => {
                  setPageSize(val);
                  setPage(1);
                }}
              />
              <table className="w-full" data-testid="sales-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">
                        Franchise
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Items</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Total</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Payment</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Delivery</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Date / Time</th>
                    {!isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sales.length === 0 ? (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500">No sales found.</td></tr>
                  ) : (
                    sales.map((sale) => (
                      <tr key={sale.id} className="border-b border-slate-100 hover:bg-slate-50">
                        {isSuperAdmin && (
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {sale.franchise_name || '-'}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">{sale.customer_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {sale.items.map((item, i) => (<div key={i}>{item.product_name} x {item.quantity} (Rs. {item.total})</div>))}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-brand-accent">Rs. {sale.total_amount}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 text-xs font-medium ${
                            sale.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            sale.payment_status === 'unpaid' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {sale.payment_status.toUpperCase()}
                            {sale.payment_method && (
                              <span className="text-xs text-slate-500">
                               - {sale.payment_method.toUpperCase()}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {(() => {
                            if (!sale.delivery_required) return "-";

                            const status = sale.delivery_status;

                            return (
                              <div className="flex flex-col">
                                {/* STATUS INLINE */}
                                <span
                                  className={`text-xs px-2 py-1 rounded font-medium w-fit ${
                                    status === "delivered"
                                      ? "bg-green-100 text-green-700"
                                      : status === "pending"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {status.toUpperCase()}
                                </span>

                                {/* ACTION (indented) */}
                                {status === "pending" && !isSuperAdmin && (
                                  <button
                                    onClick={() => handleStatusChange(sale.id, "delivered")}
                                    className="mt-1 ml-1 text-sm text-green-600 hover:text-green-700 font-medium text-left hover:underline"
                                  >
                                    Mark Delivered
                                  </button>
                                )}

                                {status === "delivered" && !isSuperAdmin && (
                                  <button
                                    onClick={() => handleStatusChange(sale.id, "pending")}
                                    className="mt-1 ml-1 text-sm text-yellow-600 hover:text-yellow-700 font-medium text-left hover:underline"
                                  >
                                    Revert
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">{formatDateTime(sale.sale_date)}</td>
                        {!isSuperAdmin && (
                          <td className="px-6 py-4 text-left text-sm text-slate-600">
                            <button onClick={() => handleEdit(sale)}
                              className="inline-flex items-center py-1 text-sm text-brand-accent hover:text-brand-accent-hover mr-2"
                              data-testid={`edit-sale-${sale.id}`}>
                              <Edit className="h-4 w-4 mr-1" /> Edit
                            </button>
                            <button onClick={() => handleDeleteClick(sale.id)}
                              className="inline-flex items-center pl-3 py-1 text-sm text-red-600 hover:text-red-700"
                              data-testid={`delete-sale-${sale.id}`}>
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <TableBottomControls
                currentPage={page}
                totalPages={totalPages}
                setCurrentPage={setPage}
                startItem={(page - 1) * pageSize + 1}
                endItem={Math.min(page * pageSize, salesTotal)}
                totalItems={salesTotal}
              />
            </div>
              
          </div>
        )}

        <ConfirmModal
          open={confirmOpen}
          title="Delete Sale"
          message="Are you sure you want to delete this sale? This action cannot be undone."
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmText="Delete"
        />
      </main>
    </AdminLayout>
  );
}
